import axios from "../../utils/axios";
import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from "@reduxjs/toolkit";
import { revokeNotificationToken } from "../../services/notifications";

const initialState = {
  user: null,
  loading: false,
  error: null,
  isAuthChecked: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.error = null;
    },
    clearUser: (state) => {
      state.user = null;
      state.error = null;
    },
    setAuthChecked: (state, action) => {
  state.isAuthChecked = action.payload;
},
    setLoading: (state, action) => {
      state.loading = !!action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload || null;
    },
  },
});

export const { setUser, clearUser, setLoading, setError, setAuthChecked } = userSlice.actions;
// ────────────────────────────────────────────────
// Core profile fetch
// ────────────────────────────────────────────────
export const fetchUser = () => async (dispatch) => {
  try {
    dispatch(setLoading(true));

    const res = await axios.get(`/users/profile`);

    dispatch(setUser(res.data.data?.user || res.data.user || null));
    dispatch(setError(null));

  } catch (err) {

    const status = err.response?.status;
    let message = 'Failed to load profile';

    if (status === 401) {
      message = 'Authentication failed - please log in again';
    } else if (err.response?.data?.message) {
      message = err.response.data.message;
    }

    dispatch(setError(message));
    throw err;

  } finally {
    dispatch(setLoading(false));
    dispatch(setAuthChecked(true));
  }
};


export const login = (credentials) => async (dispatch) => {
  try {
    dispatch(setLoading(true));

    const res = await axios.post(`/users/login`, credentials);

    // console.log('Login full response:', res.data);

    const { token, user } = res.data.data || res.data || {};

    if (!token || !user) {
      throw new Error('Invalid login response');
    }

    if (user.role !== 'admin') {
      localStorage.removeItem('token');
      dispatch(clearUser());
      throw new Error('Access denied. Admin only.');
    }

    // Save token
    localStorage.setItem('token', token);
    // console.log('Token saved:', localStorage.getItem('token'));

    // Set user data immediately (this makes isAuthenticated true)
    dispatch(setUser(user || null));

    // Optional: refresh full profile (but not necessary if user is already complete)
    // await dispatch(fetchUser());

    dispatch(setError(null)); // clear any previous error

    return res.data;
  } catch (err) {
    let message = err.message || 'Login failed';

    if (err.response?.data?.message) {
      message = err.response.data.message;
    }

    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const register = (formData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const config = formData instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    const res = await axios.post(`/users/register`, formData, config);
    await dispatch(fetchUser());
    return res.data;
  } catch (err) {
    const message = err.response?.data?.message || 'Registration failed';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

// export const updateProfile = (data) => async (dispatch) => {
//   try {
//     dispatch(setLoading(true));
//     const config = data instanceof FormData
//       ? { headers: { 'Content-Type': 'multipart/form-data' } }
//       : {};
//     const res = await axios.patch(`/users/profile`, data, config);
//     await dispatch(fetchUser());
//     return res.data;
//   } catch (err) {
//     const message = err.response?.data?.message || 'Profile update failed';
//     dispatch(setError(message));
//     throw new Error(message);
//   } finally {
//     dispatch(setLoading(false));
//   }
// };

// NEW - Verify Email OTP
export const verifyEmailOTP = ({ email, otp }) => async (dispatch) => {
  try {
    dispatch(setLoading(true));

    const res = await axios.post(`/users/verify-email-otp`, { email, otp });

    const { token, user } = res.data.data || res.data;

    if (token) {
      localStorage.setItem('token', token);
    }

    dispatch(setUser(user || null));
    dispatch(setError(null));

    return res.data;
  } catch (err) {
    const message = err.response?.data?.message || 'OTP verification failed';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const updateProfile = (data) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true));

    const res = await axios.patch(`/users/profile`, data);

    const updatedUser = res.data?.data?.user || res.data?.user;
    if (updatedUser && typeof updatedUser === 'object') {
      dispatch(setUser({
        ...(getState().user.user || {}),
        ...updatedUser,
      }));
    } else {
      // Some deployments return only a success message for profile updates.
      // Fetch the canonical profile rather than erasing the authenticated user.
      await dispatch(fetchUser());
    }

    return res.data;

  } catch (err) {
    const message = err.response?.data?.message || 'Profile update failed';
    dispatch(setError(message));
    throw new Error(message);

  } finally {
    dispatch(setLoading(false));
  }
};

export const changePassword = (data) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const res = await axios.patch(`/users/password`, data);
    return res.data;
  } catch (err) {
    const message = err.response?.data?.message || 'Password change failed';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const deleteProfile = () => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const res = await axios.delete(`/users/profile`);
    localStorage.removeItem('token');
    void revokeNotificationToken();
    dispatch(clearUser());
    dispatch(setAuthChecked(true));
    return res.data;
  } catch (err) {
    const message = err.response?.data?.message || 'Account deletion failed';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const forgetPassword = (email) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const res = await axios.post(`/users/forget`, { email });
    return res.data;
  } catch (err) {
    const message = err.response?.data?.message || 'Failed to send reset code';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const resetPassword = ({ email, password, otp }) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const res = await axios.post(`/users/reset`, { email, password, otp });
    return res.data;
  } catch (err) {
    const message = err.response?.data?.message || 'Password reset failed';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const logout = () => (dispatch) => {
  localStorage.removeItem('token');
  void revokeNotificationToken();
  dispatch(clearUser());
  dispatch(setAuthChecked(true));
};

// ────────────────────────────────────────────────
// Admin Respond to Business Verification
// ────────────────────────────────────────────────

export const respondToBusinessVerification = createAsyncThunk(
  "users/respondToBusinessVerification",
  async ({ userId, action, rejectionReason }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(
        `/users/admin/business/${userId}/verification`,
        { action, rejectionReason }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to respond to verification request"
      );
    }
  }
);

// Selectors
export const selectUser = (state) => state.user.user;
export const selectUserLoading = (state) => state.user.loading;
export const selectUserError = (state) => state.user.error;
export const selectIsAuthenticated = (state) => !!state.user.user;
export const selectIsAuthChecked = (state) => state.user.isAuthChecked;

export default userSlice.reducer;
